export type TimeSpan = string;

export type Reference = { id: string; name?: string | null };

export type PitchReference = {
  id: number;
  name: string;
  fieldLength?: number | null;
  fieldWidth?: number | null;
};

export type VideoTranscodeStatus = {
  state?: string | null;
  streamUrl?: string | null;
  error?: string | null;
};

export type VideoValidationResult = {
  bitrateResult?: string | null;
  bitrateValid: boolean;
  bitrateType?: string | null;
  resolutionResult?: string | null;
  resolutionValid: boolean;
  resolutionType?: string | null;
  codecResult?: string | null;
  codecValid: boolean;
  codecType?: string | null;
  framerateResult?: string | null;
  framerateValid: boolean;
  framerateType?: string | null;
  durationResult?: string | null;
  durationValid: boolean;
  durationType?: string | null;
  isValid: boolean;
};

export type Video = {
  id: number;
  url: string;
  sessionId: number;
  bitrate: number;
  encoding?: string | null;
  fps: number;
  length: TimeSpan;
  height: number;
  width: number;
  calibration?: string | null;
  sortOrder: number;
  clickPoints?: string | null;
  syncOffset: TimeSpan;
  ip?: string | null;
  importState?: 'IMPORTING' | 'SUCCEEDED' | 'FAILED' | null;
  importErrorCode?: string | null;
  filename?: string | null;
  transcodeStatus?: VideoTranscodeStatus | null;
  validationResult?: VideoValidationResult | null;
};

export type LabelPosition = {
  id: number;
  classType: string;
  videoTime: TimeSpan;
  x: number;
  y: number;
  z: number;
  sessionId: number;
};

export type Period = {
  id: number;
  sessionId: number;
  type: string;
  name?: string | null;
  label?: string | null;
  startTime?: TimeSpan | null;
  startTimeInMilliseconds?: number | null;
  endTime?: TimeSpan | null;
  endTimeInMilliseconds?: number | null;
  teamLeftId?: string | null;
};

export type EventLineupEntry = { number: number; type: string };

export type SessionEvent = {
  id: number;
  reason?: string | null;
  timestamp: TimeSpan;
  type: string;
  sessionId: number;
  in?: EventLineupEntry | null;
  out?: EventLineupEntry | null;
  teamId?: string | null;
  offscreen: boolean;
};

export type PlayerReference = { id: string; name?: string | null; optaUid?: string | null };

export type LineupEntry = {
  id: number;
  inSeconds?: number | null;
  outSeconds?: number | null;
  shirtNumber: number;
  periodIn?: string | null;
  periodOut?: string | null;
  type?: string | null;
  sessionId: number;
  playerId?: string | null;
  player?: PlayerReference | null;
  substitutedId?: string | null;
  substituted?: PlayerReference | null;
  reason?: string | null;
  inEventId?: number | null;
  outEventId?: number | null;
  inEvent?: SessionEvent | null;
  outEvent?: SessionEvent | null;
};

export type LabelTaskFlag = {
  id: string;
  start: number;
  end: number;
  issue?: string | null;
  status?: string | null;
  metadata?: { [key: string]: any } | null;
};

export type LabelTask = {
  id: number;
  progress: number;
  period?: { id: number; type: string } | null;
  startTime: TimeSpan;
  endTime: TimeSpan;
  lineupEntry?: { id: number; name?: string | null; type?: string | null; shirtNumber?: number | null } | null;
  owner?: { id: number; name?: string | null } | null;
  team?: Reference | null;
  flags: LabelTaskFlag[];
  startFrame: number;
  endFrame: number;
  startX: number;
  startY: number;
  startZ: number;
  sessionId?: number | null;
  classType?: string | null;
  totalFrames?: number | null;
  framesProcessed?: number | null;
};

export type Run = {
  id: string;
  creationTime: string;
  bootTime?: string | null;
  startTime?: string | null;
  stopTime?: string | null;
  doneTime?: string | null;
  haltTime?: string | null;
  errorMessage?: string | null;
  state: string;
  userId: number;
  sessionId: number;
  exportStatus?: string | null;
  skipSeconds: number;
  stopSeconds: number;
  cluster?: string | null;
  pipelineRunId?: string | null;
  pipelineVersion?: string | null;
};

export type SessionState = {
  isCompleted: boolean;
  isError: boolean;
  isSuccess: boolean;
  message?: string | null;
  code?: string | null;
  metadata?: { [key: string]: any } | null;
};

export type SessionStates = {
  upload?: SessionState | null;
  prepare?: SessionState | null;
  run?: SessionState | null;
  label?: SessionState | null;
};

export type Session = {
  id: number;
  name?: string | null;
  creatorId: number;
  pipelineId?: number | null;
  creationTime: string;
  playerCount?: number | null;
  keeperCount?: number | null;
  syncInfo?: string | null;
  competition?: string | null;
  matchStartTime?: string | null;
  keepData?: boolean | null;
  homeKeeperColor?: string | null;
  homePlayerColor?: string | null;
  awayKeeperColor?: string | null;
  awayPlayerColor?: string | null;
  refereeColor?: string | null;
  label?: string | null;
  homeScore?: number | null;
  awayScore?: number | null;
  startAndFinishOffset: TimeSpan;
  lastRunId?: string | null;
  matchInfoUrl?: string | null;
  isFullId?: boolean | null;
  finishStatus?: string | null;
  transcodeBackend?: string | null;
  approved: boolean;
  duplicatedFromId?: number | null;
  videos: Video[];
  labelPositions: LabelPosition[];
  periods: Period[];
  events: SessionEvent[];
  labelTasks: LabelTask[];
  lineup: LineupEntry[];
  lastRun?: Run | null;
  homeTeam?: Reference | null;
  awayTeam?: Reference | null;
  pitch?: PitchReference | null;
  account?: Reference | null;
  labelAccount?: Reference | null;
  states?: SessionStates | null;
};

export type CollectionResult<T> = { total: number; items: T[] };

export const timeSpanToSeconds = (value?: TimeSpan | null): number => {
  if (!value) return 0;
  const negative = value.startsWith('-');
  const match = /^-?(?:(\d+)\.)?(\d+):(\d+):(\d+(?:\.\d+)?)$/.exec(value);
  if (!match) return 0;
  const [, days, hours, minutes, seconds] = match;
  const total = Number(days || 0) * 86400 + Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
  return negative ? -total : total;
};

export const secondsToTimeSpan = (totalSeconds: number): TimeSpan => {
  const negative = totalSeconds < 0;
  let rest = Math.abs(totalSeconds);
  const days = Math.floor(rest / 86400);
  rest -= days * 86400;
  const hours = Math.floor(rest / 3600);
  rest -= hours * 3600;
  const minutes = Math.floor(rest / 60);
  const seconds = rest - minutes * 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const secondsText = seconds.toFixed(7).padStart(10, '0');
  return `${negative ? '-' : ''}${days ? `${days}.` : ''}${pad(hours)}:${pad(minutes)}:${secondsText}`;
};

export const toMillis = (value?: string | null) => {
  if (!value) return undefined;
  const parsed = Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
  return Number.isNaN(parsed) ? undefined : parsed;
};
